/* import __COLOCATED_TEMPLATE__ from './handoff-to-external-ticket.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type HandoffToExternalTicket from 'embercom/models/operator/visual-builder/step/handoff-to-external-ticket';
import type Integration from 'embercom/models/standalone/integration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import {
  HANDOFF_PLATFORM_ICONS,
  HANDOFF_PLATFORM_DISPLAY_NAMES,
  HANDOFF_PLATFORM_COMPONENTS,
  getIntegrationHandoffDisplayName,
} from 'embercom/objects/standalone/generic-handoff-platforms';

interface Args {
  step: HandoffToExternalTicket;
  editorState: EditorState;
  isPreview?: boolean;
  isEditing?: boolean;
  isFocused?: boolean;
  onValidChange?: (isValid: boolean) => void;
  readOnly?: boolean;
}

export default class HandoffToExternalTicketComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;

  @tracked availableIntegrations: Integration[] = [];
  @tracked isLoading = false;
  @tracked loadError = false;

  get isGenericPlatformHandoffStep() {
    return HANDOFF_PLATFORM_COMPONENTS[this.args.step.integrationType];
  }

  get integrationDisplayName() {
    // First try to find by ID, then fall back to type
    let integration;

    if (!this.isGenericPlatformHandoffStep) {
      integration = this.finStandaloneService.standaloneIntegrations.find(
        (i) =>
          i.integrationId === Number(this.args.step.integrationId) &&
          i.integrationType === Number(this.args.step.integrationType),
      );
    }

    if (integration) {
      return (
        getIntegrationHandoffDisplayName(integration.integrationType) ?? integration.displayName
      );
    }
    return undefined;
  }

  get stepIcon() {
    return !this.isGenericPlatformHandoffStep
      ? 'ticket'
      : HANDOFF_PLATFORM_ICONS[this.args.step.integrationType];
  }

  get handoffToExternalTicketCopy() {
    if (this.integrationDisplayName) {
      return this.intl.t('operator.workflows.visual-builder.handoff-to-external-ticket.title', {
        integration: this.integrationDisplayName,
        htmlSafe: true,
      });
    }
    return this.intl.t(
      'operator.workflows.visual-builder.handoff-to-external-ticket.connect-integration',
    );
  }

  get nodeTitle() {
    return !this.isGenericPlatformHandoffStep
      ? this.handoffToExternalTicketCopy
      : this.intl.t('operator.workflows.visual-builder.create-external-ticket.title', {
          integration: HANDOFF_PLATFORM_DISPLAY_NAMES[this.args.step.integrationType],
        });
  }

  get bodyComponent(): string | undefined {
    return !this.isGenericPlatformHandoffStep
      ? 'standalone/external-ticket-handoff-steps/options'
      : HANDOFF_PLATFORM_COMPONENTS[this.args.step.integrationType];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToExternalTicket': typeof HandoffToExternalTicketComponent;
  }
}
